import { useMachine } from "@xstate/react";
import React, {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
} from "react";
import { getCookie } from "../../../utils/cookies";
import { isSpecificSite } from "../../../utils/isSpecificSite";
import { MyAccountContext } from "./MyAccount.context";
import { myAccountMachine } from "./MyAccount.machine";
import { evaluateIfUserCanSubmitAdoptionForm } from "./utils/Profile.utils";

export interface MyAccountContextState {
	/**
	 * Triggers a search in localStorage for a token. If found, kicks off the
	 * authentication process and gets the user profile
	 */
	isAuthenticated: boolean;
	profile: DogsTrust.Profile;
	isLoading: boolean;
	handleAddToBasket: (item: DogsTrust.SADCookie) => void;
	handleRemoveFromBasket: (
		item: DogsTrust.SADCookie,
		indexToRemove?: number,
	) => void;
	handleAddToFavourites: (item: DogsTrust.DogData, data: unknown) => void;
	handleEnquireAboutDog: (item: DogsTrust.DogData, data: unknown) => void;
	handleRemoveFromFavourites: (item: DogsTrust.DogData, data: unknown) => void;
	handleAddToInterested: (item: DogsTrust.DogData) => void;
	handleRemoveFromInterested: (item: DogsTrust.DogData) => void;
	handleUpdateAmount: (amount: number) => void;
	handleRecipientChange: (basketKey: number, isGift: boolean) => void;
	handleClearBasket: () => void;
	handleLogout: () => void;
	canSubmitAdoptionApplication: boolean;
}

/**
 * MyAccountProvider - Provider component for the MyAccount context
 *
 * This component uses an XState state machine to manage complex user authentication
 * and profile state transitions. It provides a consistent interface for account
 * operations while handling synchronization between local storage and server.
 */
export const MyAccountProvider: React.FC<PropsWithChildren> = ({
	children,
}) => {
	isSpecificSite("uk", "MyAccountProvider");

	// Initialize the state machine with implementation for services
	const [state, send] = useMachine(myAccountMachine);

	// Extract current state values
	const { profile, isAuthenticated, isLoading } = state.context;

	useEffect(() => {
		console.log("Current state:", state.value);
		console.log("Current context:", state.context);
		console.log("Current profile:", state.context.profile);
	}, [state.value, state.context]);
	// Start the state machine on mount
	useEffect(() => {
		const clearBasketCookie = getCookie("clear_basket");
		const shouldClearBasket = clearBasketCookie === "true";

		// Send the START event with the cookie value
		// The state machine now handles cookie deletion at the appropriate time
		send({ type: "START", clearBasketCookie: shouldClearBasket });
	}, [send]);

	// Monitor for changes to the clear_basket cookie
	useEffect(() => {
		// Variable to store the last known cookie value
		let lastCookieValue = getCookie("clear_basket");

		// Function to check for cookie changes
		const checkCookie = () => {
			const currentCookieValue = getCookie("clear_basket");

			// If the cookie value has changed to "true", send event to clear basket
			if (currentCookieValue === "true" && lastCookieValue !== "true") {
				console.log("clear_basket cookie detected, clearing basket");
				send({ type: "CLEAR_BASKET_COOKIE_DETECTED" });
			} else if (currentCookieValue === "true") {
				console.log("clear_basket cookie is still present with value 'true'");
			}

			// Update the last known cookie value
			lastCookieValue = currentCookieValue;
		};

		// Run an initial check immediately
		checkCookie();

		// Set up an interval to check for cookie changes (every 2 seconds)
		const intervalId = setInterval(checkCookie, 2000);

		// Clean up the interval when the component unmounts
		return () => {
			clearInterval(intervalId);
		};
	}, [send]);

	// Create handler functions that send events to the state machine
	const handleAddToBasket = useCallback(
		(sadDog: DogsTrust.SADCookie) => {
			// Use the state machine for state management
			send({ type: "ADD_TO_BASKET", dog: sadDog });
		},
		[send],
	);

	const handleRemoveFromBasket = useCallback(
		(sadDog: DogsTrust.SADCookie, indexToRemove?: number) => {
			send({ type: "REMOVE_FROM_BASKET", dog: sadDog, index: indexToRemove });
		},
		[send],
	);

	const handleAddToFavourites = useCallback(
		(dog: DogsTrust.DogData, data: unknown) => {
			send({ type: "ADD_TO_FAVOURITES", dog, data });
		},
		[send],
	);

	const handleRemoveFromFavourites = useCallback(
		(dog: DogsTrust.DogData, data: unknown) => {
			send({ type: "REMOVE_FROM_FAVOURITES", dog, data });
		},
		[send],
	);

	const handleAddToInterested = useCallback(
		(dog: DogsTrust.DogData) => {
			send({ type: "ADD_TO_INTERESTED", dog });
		},
		[send],
	);

	const handleRemoveFromInterested = useCallback(
		(dog: DogsTrust.DogData) => {
			send({ type: "REMOVE_FROM_INTERESTED", dog });
		},
		[send],
	);

	const handleClearBasket = useCallback(() => {
		send({ type: "CLEAR_BASKET" });
	}, [send]);

	const handleUpdateAmount = useCallback(
		(amount: number) => {
			send({ type: "UPDATE_AMOUNT", amount });
		},
		[send],
	);

	const handleRecipientChange = useCallback(
		(basketKey: number, isGift: boolean) => {
			send({ type: "UPDATE_RECIPIENT", basketKey, isGift });
		},
		[send],
	);

	const handleEnquireAboutDog = useCallback(
		(dog: DogsTrust.DogData, data: unknown) => {
			send({ type: "ENQUIRE_ABOUT_DOG", dog, data });
		},
		[send],
	);

	const handleLogout = useCallback(() => {
		send({ type: "LOGOUT" });
	}, [send]);

	// Compute if the user can submit adoption application based on profile
	const canSubmitAdoptionApplication = useMemo(() => {
		return evaluateIfUserCanSubmitAdoptionForm(profile?.adoptionSubmissions);
	}, [profile?.adoptionSubmissions]);

	return (
		<MyAccountContext.Provider
			value={{
				isAuthenticated,
				profile,
				isLoading,
				handleAddToBasket,
				handleRemoveFromBasket,
				handleAddToFavourites,
				handleRemoveFromFavourites,
				handleAddToInterested,
				handleEnquireAboutDog,
				handleRemoveFromInterested,
				handleUpdateAmount,
				handleRecipientChange,
				handleClearBasket,
				handleLogout,
				canSubmitAdoptionApplication,
			}}
		>
			{children}
		</MyAccountContext.Provider>
	);
};
